import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { AttendanceEmployee } from "../api/attendanceApi";

interface Props {
  employees: AttendanceEmployee[];
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  className?: string;
}

/** Searchable single-select for picking one employee — the AttendanceActionModal's
 * "Add Missing Punch" flow, where typing a raw employee code by hand risked typos
 * an unrecognized code would only surface as a 4xx after submit. Single-select
 * sibling of EmployeeMultiSelect (same search-panel structure, closes on pick
 * instead of staying open for multiple toggles). */
export default function EmployeeSelect({ employees, value, onChange, placeholder = "Search employees…", className = "" }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => e.name.toLowerCase().includes(q) || e.employee_code.toLowerCase().includes(q));
  }, [employees, query]);

  const selected = useMemo(() => employees.find((e) => e.employee_code === value), [employees, value]);

  function pick(code: string) {
    onChange(code);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-2 w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 hover:border-muted-foreground transition-colors"
      >
        <span className={`truncate text-left ${!selected ? "text-muted-foreground" : ""}`}>
          {selected ? `${selected.name} (${selected.employee_code})` : value || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[240px] bg-surface-muted border border-border rounded-xl shadow-2xl overflow-hidden">
          <div className="relative p-2 border-b border-border">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or employee code"
              className="w-full pl-7 pr-2 py-1.5 bg-surface border border-border rounded-lg text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground font-inter">No employees found.</p>
            ) : (
              filtered.map((e) => (
                <button
                  type="button"
                  key={e.id}
                  onClick={() => pick(e.employee_code)}
                  className={`flex items-center w-full gap-2 px-3 py-2 text-sm text-left font-inter hover:bg-surface transition-colors ${
                    e.employee_code === value ? "bg-surface text-foreground" : "text-foreground"
                  }`}
                >
                  <span className="truncate">{e.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground font-mono shrink-0">{e.employee_code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
